import React, { useState, useEffect, useMemo } from "react";
import { Formik, Form, FormikHelpers } from "formik";
import * as Yup from "yup";

//MUI
import Stack from "@mui/material/Stack";
import Button from "@mui/material/Button";

import FormikControl from "../../components/formik/FormikControl";
import Loader from "../../components/loader/Loader";

import SegmentClassificationMapService from "../../services/SegmentClassificationMapService";
import { Mode } from "./SegmentClassificationMapPage";
import { SegmentClassificationMapModel } from "../../models/SegmentClassificationMapModel";
// import { statusOptions } from "../../utility/utils";
import { fetchSegmentClassificationMap } from "../../redux/actions/SegmentClassificationMapActions";
import { useAppDispatch, useAppSelector } from "../../redux/hooks/redux-hooks";
import { createAutocompleteOptions } from "../../utility/utils";
import ConfirmDialog from "../ConfirmDialog";

const validationSchema = Yup.object({
  segmentLOBId: Yup.number().min(1, "Required").required("Required"),
  customerTypeId: Yup.number().min(1, "Required").required("Required"),
  segmentClassification: Yup.string().required("Required"),
  // status: Yup.string().required("Required"),
});

type FormProps = Yup.InferType<typeof validationSchema>;

type Props = {
  mode: Mode;
  handleModalClose: () => void;
  data?: SegmentClassificationMapModel;
};

const SegmentClassificationMapForm = ({
  mode,
  handleModalClose,
  data,
}: Props) => {
  const initialValues = useMemo(
    () => ({
      segmentLOBId: 0,
      customerTypeId: 0,
      segmentClassification: "",
      // status: "",
    }),
    []
  );

  const [formState, setFormState] = useState<FormProps>(initialValues);
  const dispatch = useAppDispatch();
  const displayToast = useAppSelector((state) => state.toast.displayToast);
  const allCustomerTypes = useAppSelector(
    (state) => state.customerType.all_CustomerTypes
  );
  const allSegmentLOBs = useAppSelector(
    (state) => state.segmentLOB.all_SegmentLOBs
  );
  const allSegmentClassifications = useAppSelector(
    (state) => state.segmentClassification.all_SegmentClassifications
  );

  const customerTypeOptions = useMemo(() => {
    return createAutocompleteOptions(
      allCustomerTypes,
      "customerTypeId",
      "name"
    );
  }, [allCustomerTypes]);

  const segmentLOBOptions = useMemo(() => {
    return createAutocompleteOptions(allSegmentLOBs, "segmentlobid", "name");
  }, [allSegmentLOBs]);

  const segmentClassificationOptions = useMemo(() => {
    return createAutocompleteOptions(allSegmentClassifications, "name", "name");
  }, [allSegmentClassifications]);

  useEffect(() => {
    if (mode === "delete" && data) {
      setFormState(() => ({
        segmentLOBId: data.segmentLobid,
        customerTypeId: data.customerTypeId,
        segmentClassification: data.segmentClassification,
        // status: data.isActive ? "active" : "inactive",
      }));
    }
  }, [mode, data]);

  const submitHandler = async (
    values: FormProps,
    actions: FormikHelpers<FormProps>
  ) => {
    try {
      let formData: SegmentClassificationMapModel = {
        segmentLobid: 0,
        customerTypeId: 0,
        segmentClassification: "",
        isActive: true,
        createdDate: new Date(),
        modifiedDate: new Date(),
      };
      if (mode === "add") {
        formData.segmentLobid = values.segmentLOBId;
        formData.customerTypeId = values.customerTypeId;
        formData.segmentClassification = values.segmentClassification;
        // formData.isActive = values.status === "active" ? true : false;
      } else if (data) {
        formData = {
          ...data,
          segmentLobid: values.segmentLOBId,
          customerTypeId: values.customerTypeId,
          segmentClassification: values.segmentClassification,
          // isActive: values.status === "active" ? true : false,
          modifiedDate: new Date(),
        };
      }
      mode === "delete"
        ? await SegmentClassificationMapService.deleteSegmentClassificationMap(
            formData
          )
        : await SegmentClassificationMapService.createSegmentClassificationMap(
            formData
          );
      actions.setSubmitting(false);
      handleModalClose();
      dispatch(fetchSegmentClassificationMap());
      displayToast(
        `Segment classification mapping ${
          mode === "add" ? "created" : "deleted"
        } successfully`,
        "success"
      );
    } catch (error) {
      actions.setSubmitting(false);
    }
  };
  return (
    <Formik
      initialValues={formState}
      validationSchema={validationSchema}
      onSubmit={submitHandler}
      enableReinitialize={true}
    >
      {(formik) => (
        <>
          <Form>
            {mode === "delete" ? (
              <ConfirmDialog handleModalClose={handleModalClose} />
            ) : (
              <Stack gap={2}>
                <FormikControl
                  control="autocomplete"
                  options={segmentLOBOptions}
                  name="segmentLOBId"
                  size="small"
                  label="Segment LOB"
                  placeholder="Select Segment LOB"
                />
                <FormikControl
                  control="autocomplete"
                  options={customerTypeOptions}
                  name="customerTypeId"
                  size="small"
                  label="Customer Type"
                  placeholder="Select Customer Type"
                />
                <FormikControl
                  control="autocomplete"
                  options={segmentClassificationOptions}
                  name="segmentClassification"
                  size="small"
                  label="Segment Classification"
                  placeholder="Select Segment Classification"
                />
                {/* <FormikControl
                  control="autocomplete"
                  options={statusOptions}
                  name="status"
                  size="small"
                  label="Status"
                  placeholder="Select Status"
                /> */}
                <Button
                  type="submit"
                  variant="contained"
                  color="primary"
                  disabled={formik.isSubmitting}
                >
                  Submit
                </Button>
              </Stack>
            )}
          </Form>
          {formik.isSubmitting && <Loader />}
        </>
      )}
    </Formik>
  );
};

export default SegmentClassificationMapForm;
